// ─────────────────────────────────────────────────────────────────────────────
// Report dataset cache — so clicking through reports does not re-buy the data.
//
// THE PROBLEM THIS SOLVES. Every report fetches on mount, and ReportEngine
// remounts whenever the selected report changes. Several of the domains behind
// those reports fan out ONE PointClickCare call PER RESIDENT on the server —
// conditions, medications, coverages, observations, episodes, ggFunction, and
// adl when its snapshot store is cold. On a 222-bed facility that is ~222 calls
// for one report view. Opening "Resident Diagnoses", then "Average Medications
// per Resident", then going back, bought the same rows three times.
//
// Nothing was wrong with the data. It was being paid for again and again.
//
// WHAT IT DOES. Holds each (source, domain, facility) result for the session.
// The second open is instant and free. Refresh is the way to re-pull, and it is
// the ONLY way — an explicit button a person presses when they want new data,
// rather than an accident of navigation.
//
// WHY SESSION, NOT A TTL. A TTL decides for you, and it expires mid-demo or
// mid-morning-round for no reason the person can see. Refresh is legible: what
// is on screen is what was fetched, the report says when, and pressing Refresh
// gets new data. Closing the tab ends the session and the next one starts cold.
// ─────────────────────────────────────────────────────────────────────────────

const KEY_PREFIX = "survai_ds_";
const mem = new Map(); // key -> { rows, at, generation }

// Above this, an entry stays in memory but is not written to sessionStorage:
// a per-resident domain over a large facility serialises to megabytes, and
// sessionStorage has a few to spend for the whole origin. Blowing that budget
// would evict entries we care about to store one we do not.
const MAX_PERSIST_BYTES = 400 * 1024;

// Bumped by clearDatasetCache. Entries from an older generation are ignored
// rather than hunted down, which keeps the clear O(1) and makes an in-flight
// request that lands after a Refresh unable to repopulate stale rows.
let generation = 0;

export function datasetKey(domain, facilityCcn, source) {
  return `${source || "auto"}|${domain}|${facilityCcn || "ALL"}`;
}

export function getCachedDataset(domain, facilityCcn, source) {
  const key = datasetKey(domain, facilityCcn, source);
  const hit = mem.get(key);
  if (hit) return hit.generation === generation ? hit : null;
  try {
    const raw = sessionStorage.getItem(KEY_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // A survivor of a page reload: adopt it, but only into the CURRENT
    // generation, so it is cleared by the next Refresh like anything else.
    if (!parsed || !Array.isArray(parsed.rows)) return null;
    const entry = { rows: parsed.rows, at: parsed.at, generation };
    mem.set(key, entry);
    return entry;
  } catch {
    return null; // private mode, quota, corrupt entry — the cache is optional
  }
}

export function setCachedDataset(domain, facilityCcn, source, rows) {
  const key = datasetKey(domain, facilityCcn, source);
  const entry = { rows, at: new Date().toISOString(), generation };
  mem.set(key, entry);
  try {
    const payload = JSON.stringify({ rows, at: entry.at });
    if (payload.length <= MAX_PERSIST_BYTES) sessionStorage.setItem(KEY_PREFIX + key, payload);
  } catch { /* memory-only is a fine outcome */ }
  return entry;
}

/** When this dataset was fetched, or null if it is not cached. */
export function cachedAt(domain, facilityCcn, source) {
  const hit = getCachedDataset(domain, facilityCcn, source);
  return hit ? hit.at : null;
}

// WHEN A REFRESH HAPPENED, SO THE SERVER CAN BE TOLD.
//
// Clearing the browser's copy is only half of it: the server keeps its own, and
// answering a Refresh out of that makes the button a liar. The loads a Refresh
// kicks off all fire within a second or two of it, so a short window is enough
// to mark them — and it closes long before anyone navigates somewhere else on
// purpose. If it ever did leak into one extra load, the cost is one refetch,
// which is exactly what Refresh does anyway.
const FRESH_WINDOW_MS = 20000;
let freshUntil = 0;
export function isFreshWindow() { return Date.now() < freshUntil; }

/** Drop everything. Called by Refresh — the one thing that re-buys data. */
export function clearDatasetCache() {
  generation += 1;
  freshUntil = Date.now() + FRESH_WINDOW_MS;
  mem.clear();
  try {
    const doomed = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(KEY_PREFIX)) doomed.push(k);
    }
    doomed.forEach((k) => sessionStorage.removeItem(k));
  } catch { /* nothing to clear */ }
}

// ── What a refresh actually costs ───────────────────────────────────────────
//
// Mirrors the server's builders (pccIntegration.js). These domains call
// fetchPerResident, which is the facility roster plus one call for every
// resident on it — so a refresh of one of these reports on a 222-bed facility
// is over two hundred calls against the daily quota. That is worth saying on
// the screen before somebody presses the button, not discovering afterwards.
// Checked against each builder rather than assumed: assessments and
// progressNotes fan out too (buildAssessments, buildProgressNotes), while
// census is roster-only and episodes is a single facility call behind its own
// TTL cache — neither costs per resident.
export const PER_RESIDENT_DOMAINS = new Set([
  "conditions", "medications", "coverages", "observations", "transfers",
  "immunizations", "documents", "assessments", "progressNotes",
  "ggFunction", "adl", "hipps",
]);

// Worse than per-resident: the roster, then one call per resident for the
// assessment list, then one more for EVERY assessment's detail. On a facility
// mid-cycle that is the most expensive read in the product.
export const PER_ASSESSMENT_DOMAINS = new Set(["ggFunction", "hipps"]);

// Served out of Supabase from webhook deliveries — no PointClickCare call at
// read time at all. adl and hipps fall back to a live per-resident pull only
// when their snapshot store is still cold.
export const SNAPSHOT_DOMAINS = new Set(["incidents", "adl", "hipps"]);
